import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CookingPot, Plus, Trash, PencilSimple, MagnifyingGlass, Globe, User, Tag, FunnelSimple, Basket, X, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

// Food autocomplete component
function FoodAutocomplete({ search, onSelect }) {
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/foods?search=${encodeURIComponent(search)}&limit=8`);
        setResults(data);
      } catch {}
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  if (results.length === 0) return null;
  return (
    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-brand-border rounded-xl shadow-lg max-h-40 overflow-y-auto" data-testid="food-autocomplete">
      {results.map(f => (
        <button key={f.id} type="button" onClick={() => onSelect(f)} className="w-full text-left px-3 py-2 text-sm hover:bg-brand-mint/10 transition-colors border-b border-brand-border/30 last:border-0" data-testid={`autocomplete-food-${f.id}`}>
          <span className="text-brand-text">{f.name}</span>
          <span className="text-[10px] text-brand-muted ml-2">{f.energia_kcal}kcal | P:{f.proteina_g}g</span>
        </button>
      ))}
    </div>
  );
}

// Unit dropdown that includes the household measures stored in food_measures for this food
const BASE_UNITS = ['g', 'ml', 'unidade', 'colher de sopa', 'colher de cha', 'chavena', 'fatia', 'punhado', 'pitada'];
function IngredientUnitSelect({ foodId, value, onChange, testid }) {
  const [measures, setMeasures] = useState([]);
  useEffect(() => {
    if (!foodId) { setMeasures([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/foods/${encodeURIComponent(foodId)}/measures`);
        if (!cancelled) setMeasures(Array.isArray(data) ? data : (data.measures || []));
      } catch { if (!cancelled) setMeasures([]); }
    })();
    return () => { cancelled = true; };
  }, [foodId]);
  // Combine base units + DB measures (deduped, DB names normalized)
  const dbMeasureNames = Array.from(new Set(measures.map(m => (m.measure_name || '').trim()).filter(Boolean)));
  const extras = dbMeasureNames.filter(n => !BASE_UNITS.includes(n.toLowerCase()));
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-brand-border rounded-xl px-2 py-2 text-sm bg-white text-brand-text"
      data-testid={testid}
    >
      <optgroup label="Unidades padrão">
        {BASE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
      </optgroup>
      {extras.length > 0 && (
        <optgroup label="Medidas caseiras deste alimento">
          {extras.map(m => {
            const dbEntry = measures.find(x => (x.measure_name || '').trim() === m);
            const g = dbEntry?.grams_equivalent;
            return <option key={m} value={m}>{m}{g ? ` (~${g}g)` : ''}</option>;
          })}
        </optgroup>
      )}
    </select>
  );
}

const CATEGORIES = [
  { value: '', label: 'Todas' },
  { value: 'pequeno-almoco', label: 'Peq.-almoço' },
  { value: 'almoco', label: 'Almoço' },
  { value: 'jantar', label: 'Jantar' },
  { value: 'lanche', label: 'Lanche' },
  { value: 'ceia', label: 'Ceia' },
];

const DEFAULT_TAG_COLORS = {
  carne: 'bg-red-50 text-red-600', peixe: 'bg-blue-50 text-blue-600', ovo: 'bg-amber-50 text-amber-600',
  vegetariano: 'bg-emerald-50 text-emerald-600', leguminosas: 'bg-orange-50 text-orange-600',
  'rápido': 'bg-yellow-50 text-yellow-700', rapido: 'bg-yellow-50 text-yellow-700',
  infantil: 'bg-pink-50 text-pink-600',
  'pós-parto': 'bg-purple-50 text-purple-600', 'pos-parto': 'bg-purple-50 text-purple-600',
  gravidez: 'bg-rose-50 text-rose-600',
  'batch cooking': 'bg-teal-50 text-teal-600',
  'família': 'bg-indigo-50 text-indigo-600', familia: 'bg-indigo-50 text-indigo-600',
  'sem gluten': 'bg-lime-50 text-lime-700', 'sem lactose': 'bg-sky-50 text-sky-600',
};

function getTagColor(tag) {
  return DEFAULT_TAG_COLORS[tag] || 'bg-brand-bg text-brand-muted';
}

export default function ReceitasPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isNutri = user?.role === 'admin' || user?.role === 'nutricionista';
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [viewFilter, setViewFilter] = useState('todas'); // todas, globais, minhas
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [ingredients, setIngredients] = useState([{ name: '', quantity: '', unit: '' }]);
  const [formCategory, setFormCategory] = useState('');
  const [formCategories, setFormCategories] = useState([]);  // multi-category
  const [formPhotoPath, setFormPhotoPath] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [formTags, setFormTags] = useState([]);
  const [formIsPublic, setFormIsPublic] = useState(true);
  const [loading, setLoading] = useState(true);
  const [viewRecipe, setViewRecipe] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [formServings, setFormServings] = useState(1);
  const [activeIngIdx, setActiveIngIdx] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  // Ingredient search state
  const [showIngSearch, setShowIngSearch] = useState(false);
  const [ingInput, setIngInput] = useState('');
  const [selectedIngs, setSelectedIngs] = useState([]);
  const [ingResults, setIngResults] = useState([]);
  const [ingSearching, setIngSearching] = useState(false);
  const [ingCategory, setIngCategory] = useState('');
  const [ingTag, setIngTag] = useState('');

  const fetchRecipes = useCallback(async () => {
    try {
      let url = `${API}/api/recipes?search=${search}`;
      if (category) url += `&category=${category}`;
      if (activeTag) url += `&tag=${activeTag}`;
      const { data } = await axios.get(url);
      setRecipes(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, category, activeTag]);

  const fetchTags = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/recipe-tags`); setAllTags(data.tags || []); } catch {}
  }, []);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleAddNewTag = async () => {
    const tag = newTagInput.trim().toLowerCase();
    if (!tag || allTags.includes(tag)) { setNewTagInput(''); return; }
    const updated = [...allTags, tag];
    try {
      await axios.put(`${API}/api/recipe-tags`, { tags: updated });
      setAllTags(updated);
      setFormTags([...formTags, tag]);
      setNewTagInput('');
    } catch { toast.error('Erro ao adicionar tag'); }
  };

  const resetForm = () => {
    setName(''); setInstructions(''); setIngredients([{ name: '', quantity: '', unit: 'g', food_id: null, _search: '' }]);
    setFormCategory(''); setFormTags([]); setFormIsPublic(isNutri); setEditing(null);
    setFormServings(1); setActiveIngIdx(null);
    setFormCategories([]); setFormPhotoPath('');
  };

  const openEdit = (recipe) => {
    setEditing(recipe); setName(recipe.name); setInstructions(recipe.instructions || '');
    const ings = recipe.ingredients?.length > 0
      ? recipe.ingredients.map(i => ({ ...i, _search: i.name || i.food_name || '' }))
      : [{ name: '', quantity: '', unit: 'g', food_id: null, _search: '' }];
    setIngredients(ings);
    setFormCategory(recipe.category || ''); setFormTags(recipe.tags || []);
    setFormIsPublic(recipe.is_public ?? isNutri); setFormServings(recipe.servings || 1);
    setFormCategories(recipe.categories || (recipe.category ? [recipe.category] : []));
    setFormPhotoPath(recipe.photo_path || '');
    setShowForm(true);
  };

  const toggleTag = (tag) => {
    setFormTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const toggleFormCategory = (value) => {
    setFormCategories(prev => prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, fd);
      setFormPhotoPath(data.path || data.url || '');
    } catch { toast.error('Erro ao carregar foto'); }
    finally { setPhotoUploading(false); }
  };

  const addIngredient = () => setIngredients([...ingredients, { name: '', quantity: '', unit: 'g', food_id: null, _search: '' }]);
  const updateIngredient = (idx, field, val) => { const u = [...ingredients]; u[idx] = { ...u[idx], [field]: val }; setIngredients(u); };
  const removeIngredient = (idx) => { if (ingredients.length <= 1) return; setIngredients(ingredients.filter((_, i) => i !== idx)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // PROMPT 4 — Item 2: limpa também _editing_name; preserva custom_name vazio só se foi explicitamente removido
    const cleaned = ingredients.filter(i => i.name?.trim() || i.food_id).map(({ _search, _editing_name, ...rest }) => {
      // Se custom_name está vazio, remove-o para que o GET use o nome original.
      if (!rest.custom_name || !String(rest.custom_name).trim()) {
        const { custom_name: _unused, ...withoutCustom } = rest;
        return withoutCustom;
      }
      return rest;
    });
    const payload = { name, instructions, ingredients: cleaned, is_public: isNutri ? formIsPublic : false, category: formCategories[0] || formCategory, categories: formCategories, photo_path: formPhotoPath || null, tags: formTags, servings: formServings };
    try {
      if (editing) {
        await axios.put(`${API}/api/recipes/${editing.id}`, payload);
        toast.success('Receita atualizada');
      } else {
        await axios.post(`${API}/api/recipes`, payload);
        toast.success('Receita criada');
      }
      setShowForm(false); resetForm(); fetchRecipes();
    } catch (err) { toast.error('Erro ao guardar receita'); }
  };

  const handleDelete = async (id) => {
    const recipe = recipes.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a receita "${recipe?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/recipes/${id}`); toast.success('Receita eliminada'); fetchRecipes(); }
    catch { toast.error('Erro ao eliminar'); }
  };

  // Ingredient search
  const addSearchIng = () => {
    const val = ingInput.trim();
    if (val && !selectedIngs.includes(val.toLowerCase())) {
      setSelectedIngs([...selectedIngs, val.toLowerCase()]);
    }
    setIngInput('');
  };
  const removeIng = (ing) => setSelectedIngs(selectedIngs.filter(i => i !== ing));
  const searchByIngredients = async () => {
    if (selectedIngs.length === 0) return;
    setIngSearching(true);
    try {
      const { data } = await axios.post(`${API}/api/recipes/search-by-ingredients`, {
        ingredients: selectedIngs, category: ingCategory, tag: ingTag
      });
      setIngResults(data);
    } catch { toast.error('Erro na pesquisa'); }
    finally { setIngSearching(false); }
  };
  useEffect(() => { if (selectedIngs.length > 0) searchByIngredients(); else setIngResults([]); }, [selectedIngs, ingCategory, ingTag]);

  // Filter by view type
  const filtered = recipes.filter(r => {
    if (viewFilter === 'globais') return r.is_public;
    if (viewFilter === 'minhas') return r.user_id === user?.user_id;
    return true;
  });

  const globalCount = recipes.filter(r => r.is_public).length;
  const myCount = recipes.filter(r => r.user_id === user?.user_id).length;

  return (
    <div className="fade-in" data-testid="receitas-page">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="receitas-title">Receitas</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowIngSearch(!showIngSearch)} className={`rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-all ${showIngSearch ? 'bg-brand-gold text-white' : 'border border-brand-border text-brand-green hover:bg-brand-green/5'}`} data-testid="ingredient-search-toggle">
            <Basket size={16} /> O que cozinhar?
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-brand-green text-white hover:bg-brand-green-hover rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-recipe-btn">
            <Plus size={16} /> Nova
          </button>
        </div>
      </div>

      {/* ═══ INGREDIENT SEARCH ═══ */}
      {showIngSearch && (
        <div className="bg-white border border-brand-gold/30 rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(215,189,123,0.08)]" data-testid="ingredient-search-panel">
          <h3 className="font-heading font-medium text-sm text-brand-text mb-1">O que posso cozinhar hoje?</h3>
          <p className="text-xs text-brand-muted mb-4">Diga-nos o que tem no frigorífico e encontramos receitas para si e para a família.</p>

          {/* Input */}
          <div className="flex gap-2 mb-3">
            <Input
              value={ingInput}
              onChange={e => setIngInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSearchIng(); } }}
              placeholder="Ex: frango, arroz, brocolo..."
              className="rounded-2xl border-brand-border flex-1"
              data-testid="ingredient-search-input"
            />
            <button onClick={addSearchIng} className="bg-brand-gold text-white rounded-full px-4 py-2 text-sm font-medium" data-testid="add-ingredient-search">Adicionar</button>
          </div>

          {/* Selected ingredients */}
          {selectedIngs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {selectedIngs.map(ing => (
                <span key={ing} className="bg-brand-gold/15 text-brand-gold-hover text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1" data-testid={`selected-ing-${ing}`}>
                  {ing}
                  <button onClick={() => removeIng(ing)} className="hover:text-red-500"><X size={12} /></button>
                </span>
              ))}
              <button onClick={() => setSelectedIngs([])} className="text-xs text-brand-muted hover:text-red-500 px-2">Limpar tudo</button>
            </div>
          )}

          {/* Quick filters */}
          {selectedIngs.length > 0 && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {CATEGORIES.map(c => (
                <button key={c.value} onClick={() => setIngCategory(ingCategory === c.value ? '' : c.value)} className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap transition-all ${ingCategory === c.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {/* Results */}
          {ingSearching ? (
            <p className="text-center text-brand-muted text-sm py-4">A procurar...</p>
          ) : selectedIngs.length > 0 && (
            <div>
              <p className="text-xs text-brand-muted mb-3">{ingResults.length} receita{ingResults.length !== 1 ? 's' : ''} encontrada{ingResults.length !== 1 ? 's' : ''}</p>
              {ingResults.length === 0 ? (
                <p className="text-center text-brand-muted py-6 text-sm">Nenhuma receita encontrada com estes ingredientes.</p>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {ingResults.map(r => {
                    const pct = r.match_pct || 0;
                    const missing = r.missing_count || 0;
                    const missingNames = r.missing_ingredients || [];
                    return (
                      <div key={r.id} className="bg-brand-bg/50 border border-brand-border/50 rounded-2xl p-3 hover:border-brand-green/30 transition-all" data-testid={`ing-result-${r.id}`}>
                        <div className="flex items-start justify-between">
                          <button onClick={() => setViewRecipe(r)} className="flex-1 min-w-0 text-left">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-heading font-medium text-sm text-brand-text truncate">{r.name}</h4>
                              {r.is_public && <span className="text-[9px] bg-brand-gold/15 text-brand-gold-hover px-1.5 py-0.5 rounded-full flex-shrink-0">Base</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {r.category && <span className="text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">{r.category}</span>}
                              {r.tags?.slice(0, 3).map(t => <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded-full ${getTagColor(t) || 'bg-brand-bg text-brand-muted'}`}>{t}</span>)}
                            </div>
                          </button>
                          {/* Match indicator */}
                          <div className="flex-shrink-0 ml-3 text-right">
                            {missing === 0 ? (
                              <div className="flex items-center gap-1 text-brand-green">
                                <CheckCircle size={16} weight="duotone" />
                                <span className="text-xs font-semibold">Tem tudo!</span>
                              </div>
                            ) : missing === 1 ? (
                              <div className="flex items-center gap-1 text-brand-gold-hover">
                                <WarningCircle size={16} weight="duotone" />
                                <span className="text-xs font-medium">Quase completa</span>
                              </div>
                            ) : missing <= 2 ? (
                              <div className="flex items-center gap-1 text-brand-gold-hover">
                                <WarningCircle size={16} weight="duotone" />
                                <span className="text-xs font-medium">Faltam {missing} ingredientes</span>
                              </div>
                            ) : (
                              <div className="text-brand-muted">
                                <span className="text-xs">Faltam {missing} ingredientes</span>
                              </div>
                            )}
                            <div className="w-16 h-1.5 bg-brand-border rounded-full mt-1 overflow-hidden">
                              <div className={`h-full rounded-full ${pct === 100 ? 'bg-brand-green' : pct >= 60 ? 'bg-brand-gold' : 'bg-brand-muted'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[10px] text-brand-muted">{r.match_count}/{r.total_ingredients}</span>
                          </div>
                        </div>
                        {/* Missing ingredients hint */}
                        {missing > 0 && missing <= 3 && missingNames.length > 0 && (
                          <p className="text-[10px] text-brand-muted mt-1.5 pl-0.5">
                            Precisa de: {missingNames.join(', ')}
                          </p>
                        )}
                        {/* Action button - add to ementa */}
                        <div className="flex justify-end mt-2 pt-2 border-t border-brand-border/30">
                          <button
                            onClick={(e) => { e.stopPropagation(); window.location.href = '/ementa'; }}
                            className="text-[11px] text-brand-green font-medium hover:underline flex items-center gap-1"
                            data-testid={`add-to-ementa-${r.id}`}
                          >
                            <Plus size={12} /> Adicionar à ementa
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* View filters */}
      <div className="flex gap-2 mb-3">
        {[
          { key: 'todas', label: `Todas (${recipes.length})` },
          { key: 'globais', label: `Base (${globalCount})`, icon: Globe },
          { key: 'minhas', label: `Minhas (${myCount})`, icon: User },
        ].map(f => (
          <button key={f.key} onClick={() => setViewFilter(f.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${viewFilter === f.key ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`filter-${f.key}`}>
            {f.icon && <f.icon size={12} />} {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar receita..." className="pl-10 rounded-2xl border-brand-border" data-testid="recipe-search" />
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-2 rounded-2xl border transition-all flex items-center gap-1 text-sm ${showFilters || category || activeTag ? 'border-brand-green text-brand-green bg-brand-green/5' : 'border-brand-border text-brand-muted'}`} data-testid="toggle-filters">
          <FunnelSimple size={16} />
        </button>
      </div>

      {/* Category & Tag filters */}
      {showFilters && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4 space-y-3" data-testid="filters-panel">
          <div>
            <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1.5">Categoria</p>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => (
                <button key={c.value} onClick={() => setCategory(c.value)} className={`px-2.5 py-1 rounded-full text-xs transition-all ${category === c.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted hover:text-brand-text'}`} data-testid={`cat-${c.value || 'todas'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1.5">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setActiveTag('')} className={`px-2.5 py-1 rounded-full text-xs transition-all ${!activeTag ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>Todas</button>
              {allTags.map(t => (
                <button key={t} onClick={() => setActiveTag(activeTag === t ? '' : t)} className={`px-2.5 py-1 rounded-full text-xs transition-all ${activeTag === t ? 'bg-brand-green text-white' : (getTagColor(t) || 'bg-brand-bg text-brand-muted')}`} data-testid={`tag-filter-${t}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recipe list */}
      {loading ? (
        <div className="text-center py-12 text-brand-muted">A carregar...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <CookingPot size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">{search || category || activeTag ? 'Sem resultados para os filtros aplicados.' : 'Sem receitas. Comece por criar a primeira!'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(recipe => (
            <div key={recipe.id} className="bg-white border border-brand-border rounded-2xl p-4 shadow-[0_4px_16px_rgba(101,113,97,0.03)]" data-testid={`recipe-card-${recipe.id}`}>
              <div className="flex items-start justify-between">
                <button onClick={() => setViewRecipe(recipe)} className="text-left flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-medium text-brand-text truncate">{recipe.name}</h3>
                    {recipe.is_public ? (
                      <span className="flex-shrink-0 bg-brand-gold/15 text-brand-gold-hover text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-0.5"><Globe size={9} /> Base</span>
                    ) : (
                      <span className="flex-shrink-0 bg-brand-bg text-brand-muted text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-0.5"><User size={9} /> Minha</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs text-brand-muted">{recipe.ingredients?.length || 0} ingredientes</span>
                    {recipe.category && <span className="text-[10px] bg-brand-mint/15 text-brand-green px-2 py-0.5 rounded-full">{recipe.category}</span>}
                    {recipe.tags?.map(t => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded-full ${getTagColor(t) || 'bg-brand-bg text-brand-muted'}`}>{t}</span>)}
                  </div>
                </button>
                {(recipe.user_id === user?.user_id || isNutri) && (
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <button onClick={() => openEdit(recipe)} className="p-1.5 text-brand-placeholder hover:text-brand-green transition-colors" data-testid={`edit-recipe-${recipe.id}`}><PencilSimple size={15} /></button>
                    <button onClick={() => handleDelete(recipe.id)} className="p-1.5 text-brand-placeholder hover:text-red-500 transition-colors" data-testid={`delete-recipe-${recipe.id}`}><Trash size={15} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View recipe */}
      <Dialog open={!!viewRecipe} onOpenChange={() => setViewRecipe(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          {viewRecipe && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-brand-text">{viewRecipe.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {viewRecipe.is_public && <span className="bg-brand-gold/15 text-brand-gold-hover text-[10px] font-medium px-2 py-0.5 rounded-full">Base nutricionista</span>}
                {viewRecipe.category && <span className="bg-brand-mint/15 text-brand-green text-[10px] font-medium px-2 py-0.5 rounded-full">{viewRecipe.category}</span>}
                {viewRecipe.tags?.map(t => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded-full ${getTagColor(t) || 'bg-brand-bg text-brand-muted'}`}>{t}</span>)}
              </div>
              {viewRecipe.ingredients?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Ingredientes{viewRecipe.servings > 1 && ` (${viewRecipe.servings} porções)`}</h4>
                  <ul className="space-y-1">
                    {viewRecipe.ingredients.map((ing, i) => (
                      <li key={i} className="text-sm text-brand-text">&bull; <b>{ing.quantity} {ing.unit}</b> {ing.name || ing.food_name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Nutritional info - only for nutri */}
              {isNutri && viewRecipe.nutrition && (
                <div className="mb-4 bg-brand-bg rounded-2xl p-3">
                  <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Valores nutricionais{viewRecipe.servings > 1 ? ' (total)' : ''}</h4>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[['kcal', viewRecipe.nutrition.energia_kcal], ['Prot', viewRecipe.nutrition.proteina_g], ['HC', viewRecipe.nutrition.hidratos_g], ['Gord', viewRecipe.nutrition.gordura_g], ['Fibra', viewRecipe.nutrition.fibra_g]].map(([l,v]) => (
                      <div key={l}><p className="text-sm font-heading font-semibold text-brand-text">{v || 0}</p><p className="text-[10px] text-brand-muted">{l}</p></div>
                    ))}
                  </div>
                  {viewRecipe.servings > 1 && (
                    <div className="mt-2 pt-2 border-t border-brand-border/50">
                      <p className="text-[10px] text-brand-muted mb-1">Por porção ({viewRecipe.servings} porções):</p>
                      <div className="grid grid-cols-5 gap-2 text-center">
                        {[['kcal', viewRecipe.nutrition.energia_kcal], ['Prot', viewRecipe.nutrition.proteina_g], ['HC', viewRecipe.nutrition.hidratos_g], ['Gord', viewRecipe.nutrition.gordura_g], ['Fibra', viewRecipe.nutrition.fibra_g]].map(([l,v]) => (
                          <div key={l}><p className="text-sm font-heading font-semibold text-brand-green">{Math.round((v||0)/viewRecipe.servings*10)/10}</p><p className="text-[10px] text-brand-muted">{l}</p></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {viewRecipe.instructions && (
                <div>
                  <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-2">Modo de preparação</h4>
                  <p className="text-sm text-brand-text whitespace-pre-wrap">{viewRecipe.instructions}</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">{editing ? 'Editar receita' : 'Nova receita'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da receita" className="rounded-2xl border-brand-border" required data-testid="recipe-name-input" />
            </div>

            {/* Category — multi-select */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Categorias <span className="text-[10px] text-brand-placeholder">(podes selecionar várias)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.filter(c => c.value).map(c => (
                  <button key={c.value} type="button" onClick={() => toggleFormCategory(c.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${formCategories.includes(c.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`form-cat-${c.value}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Photo */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Fotografia <span className="text-[10px] text-brand-placeholder">(opcional)</span></Label>
              <div className="flex items-center gap-3">
                {formPhotoPath && (
                  <img src={`${API}/api/files/${encodeURIComponent(formPhotoPath)}`} alt="" className="w-16 h-16 object-cover rounded-xl border border-brand-border" />
                )}
                <label className="cursor-pointer border border-brand-border text-brand-muted rounded-xl px-3 py-1.5 text-xs hover:bg-brand-bg flex items-center gap-1" data-testid="recipe-photo-label">
                  {photoUploading ? 'A carregar...' : (formPhotoPath ? 'Mudar foto' : 'Adicionar foto')}
                  <input type="file" accept="image/*" onChange={e => handlePhotoUpload(e.target.files?.[0])} disabled={photoUploading} className="hidden" data-testid="recipe-photo-input" />
                </label>
                {formPhotoPath && (
                  <button type="button" onClick={() => setFormPhotoPath('')} className="text-[10px] text-brand-muted hover:text-red-500">Remover</button>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Tags</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {allTags.map(t => (
                  <button key={t} type="button" onClick={() => toggleTag(t)} className={`px-2.5 py-1 rounded-full text-xs transition-all ${formTags.includes(t) ? 'bg-brand-green text-white' : (getTagColor(t) || 'bg-brand-bg text-brand-muted border border-brand-border')}`} data-testid={`form-tag-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
              {isNutri && (
                <div className="flex gap-1.5">
                  <Input value={newTagInput} onChange={e => setNewTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewTag(); } }} placeholder="Nova tag..." className="flex-1 rounded-xl border-brand-border text-xs h-8" data-testid="new-tag-input" />
                  <button type="button" onClick={handleAddNewTag} className="text-xs text-brand-green font-medium px-2 hover:underline" data-testid="add-tag-btn">+ Adicionar</button>
                </div>
              )}
            </div>

            {/* Phase tags — only for nutri */}
            {isNutri && (
              <div data-testid="phase-tags-section">
                <Label className="text-sm text-brand-muted mb-1.5 block">Fase do programa <span className="text-[10px] text-brand-placeholder">(opcional)</span></Label>
                <div className="space-y-2">
                  {[
                    { program: 'Super Barriguinhas', items: [{key:'1t',label:'1º trimestre'},{key:'2t',label:'2º trimestre'},{key:'3t',label:'3º trimestre'}] },
                    { program: 'Super Bebés', items: [{key:'fase1_6_9m',label:'Fase 1 (6-9m)'},{key:'fase2_9_12m',label:'Fase 2 (9-12m)'},{key:'fase3_12_24m',label:'Fase 3 (12-24m)'}] },
                  ].map(group => (
                    <div key={group.program}>
                      <p className="text-[10px] uppercase tracking-wider text-brand-placeholder mb-1">{group.program}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.items.map(it => (
                          <button
                            key={it.key} type="button"
                            onClick={() => toggleTag(it.key)}
                            className={`px-2.5 py-1 rounded-full text-xs transition-all ${formTags.includes(it.key) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
                            data-testid={`form-phase-${it.key}`}
                          >
                            {it.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visibility (only for nutri) */}
            {isNutri && (
              <div>
                <Label className="text-sm text-brand-muted mb-1.5 block">Visibilidade</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setFormIsPublic(true)} className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${formIsPublic ? 'bg-brand-gold/15 text-brand-gold-hover border-2 border-brand-gold/30' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="visibility-global">
                    <Globe size={14} /> Global
                  </button>
                  <button type="button" onClick={() => setFormIsPublic(false)} className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${!formIsPublic ? 'bg-brand-green/10 text-brand-green border-2 border-brand-green/30' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="visibility-private">
                    <User size={14} /> Privada
                  </button>
                </div>
              </div>
            )}

            {/* Servings */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Número de porções</Label>
              <Input type="number" min="1" value={formServings} onChange={e => setFormServings(parseInt(e.target.value) || 1)} className="w-24 rounded-2xl border-brand-border" data-testid="recipe-servings-input" />
            </div>

            {/* Ingredients from food DB */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Ingredientes</Label>
              <div className="space-y-3">
                {ingredients.map((ing, idx) => (
                  <div key={idx} className="bg-brand-bg/50 rounded-2xl p-3 border border-brand-border/40" data-testid={`ingredient-row-${idx}`}>
                    {/* Food selection */}
                    {!ing.food_id ? (
                      <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-placeholder" />
                        <Input
                          value={ing._search || ''}
                          onChange={(e) => { updateIngredient(idx, '_search', e.target.value); setActiveIngIdx(idx); }}
                          onFocus={() => setActiveIngIdx(idx)}
                          placeholder="Procurar alimento..."
                          className="pl-8 rounded-xl border-brand-border text-sm"
                          data-testid={`ingredient-search-${idx}`}
                        />
                        {activeIngIdx === idx && ing._search && ing._search.length >= 2 && (
                          <FoodAutocomplete search={ing._search} onSelect={(food) => {
                            const updated = [...ingredients];
                            updated[idx] = { ...updated[idx], food_id: food.id, name: food.name, food_name: food.name, _search: food.name, quantity: updated[idx].quantity || '', unit: updated[idx].unit || 'g' };
                            setIngredients(updated);
                            setActiveIngIdx(null);
                          }} />
                        )}
                        {ing._search && <p className="text-[10px] text-brand-placeholder mt-1">Seleciona um alimento da lista</p>}
                      </div>
                    ) : (
                      <div>
                        {/* Selected food header */}
                        <div className="flex items-center justify-between mb-2">
                          {/* PROMPT 4 — Item 2: nome editável inline (custom_name) com fallback ao nome original */}
                          {ing._editing_name ? (
                            <div className="flex items-center gap-1 flex-1 mr-2">
                              <Input
                                autoFocus
                                value={ing.custom_name ?? ''}
                                onChange={(e) => updateIngredient(idx, 'custom_name', e.target.value)}
                                onBlur={() => updateIngredient(idx, '_editing_name', false)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); updateIngredient(idx, '_editing_name', false); } }}
                                placeholder={ing.name || 'Nome a apresentar'}
                                className="rounded-lg border-brand-border text-sm h-8 py-1"
                                data-testid={`ingredient-custom-name-input-${idx}`}
                              />
                              {ing.custom_name && (
                                <button type="button" onClick={() => { updateIngredient(idx, 'custom_name', ''); updateIngredient(idx, '_editing_name', false); }} className="text-[10px] text-brand-muted hover:text-red-500" title="Repor nome original" data-testid={`ingredient-custom-name-clear-${idx}`}>repor</button>
                              )}
                            </div>
                          ) : (
                            <button type="button" onClick={() => updateIngredient(idx, '_editing_name', true)} className="flex items-center gap-1.5 text-left group" data-testid={`ingredient-name-display-${idx}`} title="Clica para personalizar o nome">
                              <span className="text-sm font-medium text-brand-text">{ing.custom_name || ing.name}</span>
                              {ing.custom_name && ing.custom_name !== ing.name && (
                                <span className="text-[9px] text-brand-muted bg-brand-bg/60 rounded-full px-1.5 py-0.5">{ing.name}</span>
                              )}
                              <PencilSimple size={11} className="text-brand-placeholder group-hover:text-brand-green" />
                            </button>
                          )}
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => { const u=[...ingredients]; u[idx]={...u[idx], food_id:null, name:'', custom_name:'', _search:'', _editing_name:false}; setIngredients(u); }} className="text-[10px] text-brand-muted hover:text-brand-green">Alterar</button>
                            {ingredients.length > 1 && <button type="button" onClick={() => removeIngredient(idx)} className="text-red-400 hover:text-red-600 p-1"><Trash size={13} /></button>}
                          </div>
                        </div>
                        {/* Quantity + Unit row */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Input
                              value={ing.quantity || ''}
                              onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                              placeholder="Quantidade"
                              type="number"
                              step="any"
                              className="rounded-xl border-brand-border text-sm"
                              data-testid={`ingredient-qty-${idx}`}
                            />
                          </div>
                          <div className="w-44">
                            <IngredientUnitSelect
                              foodId={ing.food_id}
                              value={ing.unit || 'g'}
                              onChange={(v) => updateIngredient(idx, 'unit', v)}
                              testid={`ingredient-unit-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Remove button when no food selected */}
                    {!ing.food_id && ingredients.length > 1 && (
                      <div className="flex justify-end mt-1">
                        <button type="button" onClick={() => removeIngredient(idx)} className="text-red-400 hover:text-red-600 text-[10px]">Remover</button>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addIngredient} className="text-sm text-brand-green hover:text-brand-green-hover flex items-center gap-1" data-testid="add-ingredient-btn"><Plus size={14} /> Adicionar ingrediente</button>
              </div>
            </div>

            {/* Instructions */}
            <div>
              <Label className="text-sm text-brand-muted mb-1.5 block">Modo de preparação</Label>
              <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Descreva o modo de preparação..." className="rounded-2xl border-brand-border resize-none" rows={4} data-testid="recipe-instructions-input" />
            </div>

            <button type="submit" className="w-full bg-brand-green text-white hover:bg-brand-green-hover rounded-full px-6 py-3.5 font-medium" data-testid="save-recipe-btn">
              {editing ? 'Guardar alterações' : 'Criar receita'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
